import React, { useRef, useState, FC, Dispatch, SetStateAction, useEffect } from 'react'
import { GoogleMap, Marker, StandaloneSearchBox } from '@react-google-maps/api'

import { Coordinates } from '../../types'

interface MapComponentProps {
  setCoordinates: Dispatch<SetStateAction<Coordinates[] | undefined>>
  coordinates?: Coordinates[]
  initialCoordinates?: Coordinates[]
  defaultCenter?: { lat: number; lng: number }
  editable?: boolean
  onAddressSelect?: (address: {
    city?: string
    state?: string
    country?: string
    street?: string
    number?: string
  }) => void
  fullAddress?: string
}

// const defaultCenter = { lat: -34.6104, lng: -64.4874 }
const defaultZoom = 4

const MapComponent: FC<MapComponentProps> = ({
  coordinates,
  setCoordinates,
  initialCoordinates,
  defaultCenter,
  editable = false,
  onAddressSelect,
  fullAddress,
}) => {
  const [searchBox, setSearchBox] = useState<google.maps.places.SearchBox | null>(null)
  const [mapCenter, setMapCenter] = useState(
    initialCoordinates?.[0] ?? defaultCenter ?? { lat: -34.6104, lng: -64.4874 },
  )
  const [mapZoom, setMapZoom] = useState(initialCoordinates?.[0] ? 14 : defaultZoom)
  const inputRef = useRef<HTMLInputElement>(null)

  const mapRef = useRef<google.maps.Map | null>(null)

  const onLoadMap = (map: google.maps.Map) => {
    mapRef.current = map
  }

  useEffect(() => {
    if (fullAddress && editable) {
      const geocoder = new google.maps.Geocoder()
      geocoder.geocode({ address: fullAddress }, (results, status) => {
        if (status === 'OK' && results?.[0]) {
          const location = results[0].geometry.location
          const lat = location.lat()
          const lng = location.lng()
          setCoordinates([{ lat, lng }])
          setMapCenter({ lat, lng })
          setMapZoom(14)
        }
      })
    }
  }, [fullAddress, editable, setCoordinates])

  useEffect(() => {
    if (defaultCenter && !initialCoordinates && mapRef.current) {
      mapRef.current.panTo(defaultCenter)
      setMapCenter(defaultCenter)
      setMapZoom(5)
    }
  }, [defaultCenter, initialCoordinates])

  const handleCoordinates = (e: google.maps.MapMouseEvent) => {
    if (e.latLng && editable) {
      const lat = e.latLng.lat()
      const lng = e.latLng.lng()
      setCoordinates([{ lat, lng }])
    }
  }

  const onLoadSearchBox = (ref: google.maps.places.SearchBox) => {
    setSearchBox(ref)
  }

  const onPlacesChanged = () => {
    if (editable) {
      const places = searchBox?.getPlaces()
      if (places && places.length > 0) {
        const place = places[0]
        if (place.geometry && place.geometry.location) {
          const lat = place.geometry.location.lat()
          const lng = place.geometry.location.lng()
          setCoordinates([{ lat, lng }])

          setMapCenter({ lat, lng })
          setMapZoom(14)

          const addressComponents = place.address_components || []
          const city = addressComponents.find(comp => comp.types.includes('locality'))?.long_name
          const state = addressComponents.find(comp =>
            comp.types.includes('administrative_area_level_1'),
          )?.long_name
          const country = addressComponents.find(comp => comp.types.includes('country'))?.long_name
          const street = addressComponents.find(comp => comp.types.includes('route'))?.long_name
          const number = addressComponents.find(comp =>
            comp.types.includes('street_number'),
          )?.long_name

          if (onAddressSelect) {
            onAddressSelect({ city, state, country, street, number })
          }
        }
      }
    }
  }

  useEffect(() => {
    if (initialCoordinates && setCoordinates) {
      setCoordinates(initialCoordinates)
    }
  }, [initialCoordinates, setCoordinates])

  return (
    <GoogleMap
      onLoad={onLoadMap}
      onClick={handleCoordinates}
      center={mapCenter}
      zoom={mapZoom}
      mapContainerStyle={{
        width: '100%',
        height: '30rem',
        borderRadius: '0.75rem',
      }}
      options={{
        mapTypeControl: false,
      }}
    >
      {editable && (
        <StandaloneSearchBox onLoad={onLoadSearchBox} onPlacesChanged={onPlacesChanged}>
          <input
            type="text"
            placeholder="Buscar ubicación"
            ref={inputRef}
            style={{
              boxSizing: 'border-box',
              border: '1px solid transparent',
              width: '240px',
              height: '32px',
              padding: '0 12px',
              borderRadius: '3px',
              boxShadow: '0 2px 6px rgba(0, 0, 0, 0.3)',
              fontSize: '14px',
              outline: 'none',
              textOverflow: 'ellipsis',
              position: 'absolute',
              marginLeft: '12px',
              top: '10px',
              zIndex: 1000,
            }}
          />
        </StandaloneSearchBox>
      )}

      {coordinates?.map((position, index) => (
        <Marker
          key={position.id ?? 'pos-' + index}
          position={position}
          draggable={editable}
          onDragEnd={handleCoordinates}
        />
      ))}
    </GoogleMap>
  )
}

export default MapComponent
